/**
 * Migration Progress Provider
 *
 * Manages migration journey step state for the PagerDuty Migration
 * Assistant. Polls the same generic ``/agents/{agent_id}/threads/{thread_id}/agent-steps``
 * endpoint the cleanup agent uses, but with ``agent_id='pd_migration_assitant'``
 * and a simpler step model — no per-step issue counts, no team selection.
 *
 * Stops polling automatically when the last journey step (``report``) is
 * Complete.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useState,
} from 'react';

import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { STORAGE_KEYS } from '@/config/storage-config';
import { appConfig } from '@/config/app-config';

const MAX_RETRY_ATTEMPTS = 3;
const POLLING_INTERVAL = 5000;
const TERMINAL_STEP_NAME = 'report';

export interface MigrationStep {
  step_name: string;
  display_name: string;
  status: 'Created' | 'In progress' | 'Complete';
  step_order: number;
}

/** Status enum mirroring backend ``migration_resources.status``. */
export type MigrationResourceStatus =
  | 'pending'
  | 'in_progress'
  | 'migrated'
  | 'failed'
  | 'skipped';

export interface MigrationResource {
  resource_name: string;
  opsgenie_id: string | null;
  pagerduty_id: string | null;
  status: MigrationResourceStatus;
  error_message: string | null;
  migrated_at: string | null;
}

export interface MigrationResourceTotals {
  total: number;
  migrated: number;
  in_progress: number;
  pending: number;
  failed: number;
  skipped: number;
}

/** Per-resource-type rollup state. Drives the modal. */
export type MigrationTypeStatus =
  | 'pending'
  | 'in_progress'
  | 'complete'
  | 'failed';

export interface MigrationTypeRow {
  resource_type: string;
  status: MigrationTypeStatus;
  step_order: number;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
}

/**
 * Which life-cycle phase the modal should render right now. Scan and
 * migration are never simultaneously active on the same thread (scan runs
 * during ``discover`` and migration during ``execute``), so a single
 * derivation suffices.
 */
export type MigrationActivePhase = 'scan' | 'migration' | null;

interface MigrationProgressContextValue {
  steps: MigrationStep[];
  isLoading: boolean;
  /** The first step whose status is "In progress", or null when none is active. */
  currentStep: MigrationStep | null;
  /** Whether the final journey step (``report``) has been marked Complete. */
  isFinished: boolean;
  /** 0–100 percentage of Complete steps over total steps. */
  progressPercent: number;
  /** Resource-level rollups (Phase B). Keyed by resource_type. */
  resourcesByType: Record<string, MigrationResource[]>;
  totalsByType: Record<string, MigrationResourceTotals>;

  /** Per-resource-type rows ordered by step_order. Drives the modal. */
  migrationTypes: MigrationTypeRow[];
  /** True when any migration type row is pending or in_progress. */
  isMigrationActive: boolean;
  /** 0–100 percentage of completed migration types. */
  typesProgressPercent: number;

  /** Per-scan-type rows from ``/scan-types`` — drives the modal during scan. */
  scanTypes: MigrationTypeRow[];
  /** True when any scan type row is pending or in_progress. */
  isScanActive: boolean;
  /** 0–100 percentage of completed scan types. */
  scanProgressPercent: number;

  /** Which phase is active right now (scan, migration, or none). */
  activePhase: MigrationActivePhase;

  /** Whether the modal is open. Auto-opens when either phase is active. */
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;

  refetch: () => Promise<void>;
}

const defaultContextValue: MigrationProgressContextValue = {
  steps: [],
  isLoading: false,
  currentStep: null,
  isFinished: false,
  progressPercent: 0,
  resourcesByType: {},
  totalsByType: {},
  migrationTypes: [],
  isMigrationActive: false,
  typesProgressPercent: 0,
  scanTypes: [],
  isScanActive: false,
  scanProgressPercent: 0,
  activePhase: null,
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  refetch: async () => {},
};

const MigrationProgressContext =
  createContext<MigrationProgressContextValue>(defaultContextValue);

interface MigrationProgressProviderProps {
  children: ReactNode;
  threadId: string | null;
}

export function MigrationProgressProvider({
  children,
  threadId,
}: MigrationProgressProviderProps) {
  const [steps, setSteps] = useState<MigrationStep[]>([]);
  const [resourcesByType, setResourcesByType] = useState<
    Record<string, MigrationResource[]>
  >({});
  const [totalsByType, setTotalsByType] = useState<
    Record<string, MigrationResourceTotals>
  >({});
  const [migrationTypes, setMigrationTypes] = useState<MigrationTypeRow[]>([]);
  const [isMigrationActive, setIsMigrationActive] = useState(false);
  const [typesProgressPercent, setTypesProgressPercent] = useState(0);
  const [scanTypes, setScanTypes] = useState<MigrationTypeRow[]>([]);
  const [isScanActive, setIsScanActive] = useState(false);
  const [scanProgressPercent, setScanProgressPercent] = useState(0);

  // Modal state: auto-open on first transition to active; remember the
  // user's manual close so we don't reopen on every poll. Reset when the
  // migration finishes so the next run can pop the modal again.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userClosedRef = useRef(false);
  const prevActiveRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const retryCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSteps = useCallback(async () => {
    if (!threadId) {
      setSteps([]);
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      // Auth token may not be available yet on first render — quiet skip.
      console.warn('migration-progress: no auth token available');
      return;
    }

    setIsLoading(true);

    try {
      const deploymentUrl = appConfig.API_BASE_URL;
      // Fall back to the canonical migration agent id when the Redux slice
      // hasn't resolved an agent yet (e.g. very first paint of a deep-link).
      const agentId = selectedAgent?.id || 'pd_migration_assitant';
      const response = await fetch(
        `${deploymentUrl}/agents/${agentId}/threads/${threadId}/agent-steps`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch migration steps: ${response.status}`,
        );
      }

      const data = await response.json();

      if (isMountedRef.current) {
        const fetched: MigrationStep[] = Array.isArray(data?.steps)
          ? data.steps
          : [];
        setSteps(fetched);
        retryCountRef.current = 0;
      }
    } catch (error) {
      console.warn('Failed to fetch migration steps:', error);
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        retryCountRef.current += 1;
        setTimeout(() => {
          if (isMountedRef.current) fetchSteps();
        }, 1000 * retryCountRef.current);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [threadId, selectedAgent]);

  /**
   * Fetch the per-resource rollups. Separate from ``fetchSteps`` so a 5xx
   * on one endpoint doesn't break the other. Returns silently when no
   * threadId / token / matching rows yet — the FE renders an empty
   * rollup section in that case.
   */
  const fetchResources = useCallback(async () => {
    if (!threadId) {
      setResourcesByType({});
      setTotalsByType({});
      return;
    }
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;

    try {
      const deploymentUrl = appConfig.API_BASE_URL;
      const agentId = selectedAgent?.id || 'pd_migration_assitant';
      const response = await fetch(
        `${deploymentUrl}/agents/${agentId}/threads/${threadId}/migration-resources`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) {
        // 4xx/5xx silently — the rollup section just stays empty.
        return;
      }
      const data = await response.json();
      if (!isMountedRef.current) return;
      setResourcesByType(
        (data?.resources_by_type as Record<string, MigrationResource[]>) || {},
      );
      setTotalsByType(
        (data?.totals_by_type as Record<string, MigrationResourceTotals>) || {},
      );
    } catch (error) {
      console.warn('Failed to fetch migration resources:', error);
    }
  }, [threadId, selectedAgent]);

  /**
   * Fetch the per-resource-type rollup. Drives the modal — when any row
   * is in pending/in_progress the modal auto-opens (unless the user
   * already closed it). Silent failures keep the modal closed.
   */
  const fetchTypes = useCallback(async () => {
    if (!threadId) {
      setMigrationTypes([]);
      setIsMigrationActive(false);
      setTypesProgressPercent(0);
      return;
    }
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;

    try {
      const deploymentUrl = appConfig.API_BASE_URL;
      const agentId = selectedAgent?.id || 'pd_migration_assitant';
      const response = await fetch(
        `${deploymentUrl}/agents/${agentId}/threads/${threadId}/migration-types`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) return;
      const data = await response.json();
      if (!isMountedRef.current) return;

      const rows: MigrationTypeRow[] = Array.isArray(data?.types) ? data.types : [];
      setMigrationTypes(rows);
      setIsMigrationActive(!!data?.is_active);
      setTypesProgressPercent(
        typeof data?.progress_percent === 'number' ? data.progress_percent : 0,
      );
    } catch (error) {
      console.warn('Failed to fetch migration types:', error);
    }
  }, [threadId, selectedAgent]);

  /**
   * Fetch the per-scan-type rollup. Twin of ``fetchTypes`` but hits
   * ``/scan-types`` — the scan phase has its own table on the backend so
   * scan and migration progress never collide on the same thread.
   */
  const fetchScanTypes = useCallback(async () => {
    if (!threadId) {
      setScanTypes([]);
      setIsScanActive(false);
      setScanProgressPercent(0);
      return;
    }
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;

    try {
      const deploymentUrl = appConfig.API_BASE_URL;
      const agentId = selectedAgent?.id || 'pd_migration_assitant';
      const response = await fetch(
        `${deploymentUrl}/agents/${agentId}/threads/${threadId}/scan-types`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) return;
      const data = await response.json();
      if (!isMountedRef.current) return;

      const rows: MigrationTypeRow[] = Array.isArray(data?.types) ? data.types : [];
      setScanTypes(rows);
      setIsScanActive(!!data?.is_active);
      setScanProgressPercent(
        typeof data?.progress_percent === 'number' ? data.progress_percent : 0,
      );
    } catch (error) {
      console.warn('Failed to fetch scan types:', error);
    }
  }, [threadId, selectedAgent]);

  // Fetch on mount + when threadId changes (all four streams)
  useEffect(() => {
    isMountedRef.current = true;
    fetchSteps();
    fetchResources();
    fetchTypes();
    fetchScanTypes();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchSteps, fetchResources, fetchTypes, fetchScanTypes]);

  // Derived "everything's done" signal — the journey's terminal step
  // (``report``) being Complete trumps everything else. We use it to
  // unstick the modal/pill if a per-type ``complete`` webhook was
  // dropped (e.g. backend restart mid-run) and left a row in
  // ``in_progress`` forever. Computed here so the open/close effect
  // can react to it.
  const _terminalDone =
    steps.find((s) => s.step_name === TERMINAL_STEP_NAME)?.status === 'Complete';

  // "Any phase is active right now". The auto-open effect listens to
  // this combined signal so both scan and migration can pop the modal
  // independently.
  const _anyActive = isScanActive || isMigrationActive;

  // Auto-open the modal the first time we see any phase flip from
  // false → true. Honors the user's manual close (userClosedRef). When
  // both phases go quiet we reset userClosedRef so the next phase can
  // pop the modal again. Also auto-closes when the journey's terminal
  // step lands — covers the dropped-webhook case described above.
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    if (_anyActive && !wasActive) {
      if (!userClosedRef.current) setIsModalOpen(true);
    }
    if (!_anyActive && wasActive) {
      userClosedRef.current = false;
    }
    if (_terminalDone) {
      setIsModalOpen(false);
      userClosedRef.current = false;
    }
    prevActiveRef.current = _anyActive;
  }, [_anyActive, _terminalDone]);

  // Thread switch resets manual-close memory so a fresh migration in the
  // new thread can pop the modal.
  useEffect(() => {
    userClosedRef.current = false;
    setIsModalOpen(false);
  }, [threadId]);

  // Polling — stops when the terminal step is Complete. Drives both the
  // journey-step stream and the resource-rollup stream on the same tick.
  useEffect(() => {
    if (!threadId) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      const terminal = steps.find((s) => s.step_name === TERMINAL_STEP_NAME);
      if (terminal?.status === 'Complete') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        return;
      }
      fetchSteps();
      fetchResources();
      fetchTypes();
      fetchScanTypes();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, fetchSteps, fetchResources, fetchTypes, fetchScanTypes]);

  // Derived values --------------------------------------------------------
  // Prefer the step the backend explicitly marks ``In progress``. If
  // none is in that state (common when the LLM forgot to flip a step
  // forward) fall back to the first step that's NOT yet Complete —
  // that way the sidebar highlight + recommended prompts still
  // advance with the rest of the journey. Returns null only when
  // every step is Complete.
  const currentStep =
    steps.find((step) => step.status === 'In progress') ||
    steps.find((step) => step.status !== 'Complete') ||
    null;
  const completedCount = steps.filter((s) => s.status === 'Complete').length;
  const progressPercent =
    steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;
  const terminal = steps.find((s) => s.step_name === TERMINAL_STEP_NAME);
  const isFinished = terminal?.status === 'Complete';

  // Combined refetch — useful for "Refresh" buttons or after a manual user
  // action that should bump all streams at once.
  const refetch = useCallback(async () => {
    await Promise.all([
      fetchSteps(),
      fetchResources(),
      fetchTypes(),
      fetchScanTypes(),
    ]);
  }, [fetchSteps, fetchResources, fetchTypes, fetchScanTypes]);

  const openModal = useCallback(() => {
    userClosedRef.current = false;
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    userClosedRef.current = true;
    setIsModalOpen(false);
  }, []);

  // Effective values shown to consumers. When the journey is fully done
  // we force the active flags to ``false`` and clamp the progress bars
  // to 100% so the modal/pill don't get stuck at e.g. 80% because of a
  // dropped per-type webhook. Applied per-phase since scan and migration
  // can finish at different points in the journey.
  const effectiveMigrationActive = !!isFinished ? false : isMigrationActive;
  const effectiveMigrationProgress = !!isFinished ? 100 : typesProgressPercent;
  const effectiveScanActive = !!isFinished ? false : isScanActive;
  const effectiveScanProgress = !!isFinished ? 100 : scanProgressPercent;

  // Which stream feeds the modal right now. Scan wins when its rows are
  // pending/in_progress (it runs first in the journey, before migration).
  // Once scan settles into all-complete, the active phase flips to
  // migration if migration kicks off. ``null`` when both are quiet — the
  // modal stays whichever phase last opened it, but the auto-open effect
  // won't re-pop it.
  const activePhase: MigrationActivePhase = effectiveScanActive
    ? 'scan'
    : effectiveMigrationActive
      ? 'migration'
      : null;

  const contextValue: MigrationProgressContextValue = {
    steps,
    isLoading,
    currentStep,
    isFinished: !!isFinished,
    progressPercent,
    resourcesByType,
    totalsByType,
    migrationTypes,
    isMigrationActive: effectiveMigrationActive,
    typesProgressPercent: effectiveMigrationProgress,
    scanTypes,
    isScanActive: effectiveScanActive,
    scanProgressPercent: effectiveScanProgress,
    activePhase,
    isModalOpen,
    openModal,
    closeModal,
    refetch,
  };

  return (
    <MigrationProgressContext.Provider value={contextValue}>
      {children}
    </MigrationProgressContext.Provider>
  );
}

export function useMigrationProgress(): MigrationProgressContextValue {
  return useContext(MigrationProgressContext);
}
